import React, { useState, useEffect } from "react";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  Collapse,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import {
  CalendarMonth,
  Schedule,
  Celebration,
  Upload,
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  CloudUpload,
  History,
  Close,
} from "@mui/icons-material";
import typography from "../../cssUtils/typography";
import {
  Description as DescriptionIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import ButtonOne from "../../common/normal-button";
import ButtonTwo from "../../common/dashboard-button";
import AppDropdown from "../../common/app-dropdown";
import AppTable from "../../common/app-table";
import AppChip from "../../common/app-chip";
import CustomTabs from "../../common/custom-tabs";
import { GET_PANCHANG_ENDPOINT } from "../../config/urls";
import { GET_MUHURAT_ENDPOINT } from "../../config/urls";
import { GET_FESTIVAL_VRAT_ENDPOINT } from "../../config/urls";
import { GET_UPLOAD_LOGS_ENDPOINT } from "../../config/urls";
import { GET_KALNIRNAY_DASHBOARD_STATS_ENDPOINT } from "../../config/urls";
import { PUT_CHECK_DATA_ENDPOINT } from "../../config/urls";
import { PUT_UPLOAD_ENDPOINT } from "../../config/urls";

const PanchangUI = () => {
  const panchangSampleFileURL = "/panchang_sample.csv";
  const muhuratSampleFileURL = "/muhurat_sample.csv";
  const festivalVratSampleFileURL = "/festival_vrat_sample.csv";

  // Keyed by uploadType — replaces the old panchang/muhurat binary ternary so a third
  // (or future) type doesn't require touching every call site individually.
  const SAMPLE_FILES = {
    panchang: { url: panchangSampleFileURL, filename: "panchang_sample.csv", label: "Panchang" },
    muhurat: { url: muhuratSampleFileURL, filename: "muhurat_sample.csv", label: "Muhurat" },
    festival_vrat: { url: festivalVratSampleFileURL, filename: "festival_vrat_sample.csv", label: "Festival & Vrat" },
  };

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const years = ["2024", "2025", "2026"];

  const currentMonth = months[new Date().getMonth()];
  const currentYear = new Date().getFullYear().toString();
  // State management
  const [activeTab, setActiveTab] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);

  // Upload state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadMonth, setUploadMonth] = useState(currentMonth);
  const [uploadYear, setUploadYear] = useState(currentYear);
  const [uploadType, setUploadType] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Confirmation dialog
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [existingDataInfo, setExistingDataInfo] = useState(null);

  // Results dialog
  const [resultsDialogOpen, setResultsDialogOpen] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);

  // Data state
  const [panchangData, setPanchangData] = useState([]);
  const [muhuratData, setMuhuratData] = useState([]);
  const [festivalVratData, setFestivalVratData] = useState([]);
  const [uploadLogs, setUploadLogs] = useState([]);
  const [stats, setStats] = useState(null);

  // UI state
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [uploadMessage, setUploadMessage] = useState(null);
  const [loading, setLoading] = useState(false);

  // Pagination state
  const [panchangController, setPanchangController] = useState({
    page: 0,
    rowsPerPage: 20,
  });
  const [panchangRowCount, setPanchangRowCount] = useState(0);

  const [muhuratController, setMuhuratController] = useState({
    page: 0,
    rowsPerPage: 20,
  });
  const [muhuratRowCount, setMuhuratRowCount] = useState(0);

  const [festivalVratController, setFestivalVratController] = useState({
    page: 0,
    rowsPerPage: 20,
  });
  const [festivalVratRowCount, setFestivalVratRowCount] = useState(0);

  const [logsController, setLogsController] = useState({
    page: 0,
    rowsPerPage: 20,
  });
  const [logsRowCount, setLogsRowCount] = useState(0);

  const tenant = localStorage.getItem("tenant");
  const headers = {
    "Content-Type": "application/json",
    // Authorization: `Bearer ${token}`,
    "X-Tenant-ID": tenant,
  };
  // Fetch functions
  const fetchPanchangData = async () => {
    console.log("Fetching Panchang data...");
    setLoading(true);

    try {
      const queryParams = new URLSearchParams({
        month: selectedMonth,
        year: selectedYear,
        limit: panchangController.rowsPerPage.toString(),
        offset: (
          panchangController.page * panchangController.rowsPerPage
        ).toString(),
      });

      const url = `${GET_PANCHANG_ENDPOINT}?${queryParams}`;
      console.log("Fetching from URL:", url);

      const response = await fetch(url, { headers });

      if (!response.ok) {
        throw new Error("Failed to fetch panchang data");
      }

      const jsonData = await response.json();
      console.log("GET Panchang Data:", jsonData);

      setPanchangData(jsonData.results || []);
      setPanchangRowCount(jsonData.count || 0);
    } catch (error) {
      console.error("Error fetching panchang data:", error.message);
      setPanchangData([]);
      setPanchangRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchMuhuratData = async () => {
    console.log("Fetching Muhurat data...");
    setLoading(true);

    try {
      const queryParams = new URLSearchParams({
        month: selectedMonth,
        year: selectedYear,
        limit: muhuratController.rowsPerPage.toString(),
        offset: (
          muhuratController.page * muhuratController.rowsPerPage
        ).toString(),
      });

      const url = `${GET_MUHURAT_ENDPOINT}?${queryParams}`;
      console.log("Fetching from URL:", url);

      const response = await fetch(url, { headers });

      if (!response.ok) {
        throw new Error("Failed to fetch muhurat data");
      }

      const jsonData = await response.json();
      console.log("GET Muhurat Data:", jsonData);

      setMuhuratData(jsonData.results || []);
      setMuhuratRowCount(jsonData.count || 0);
    } catch (error) {
      console.error("Error fetching muhurat data:", error.message);
      setMuhuratData([]);
      setMuhuratRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchFestivalVratData = async () => {
    console.log("Fetching Festival & Vrat data...");
    setLoading(true);

    try {
      const queryParams = new URLSearchParams({
        month: selectedMonth,
        year: selectedYear,
        limit: festivalVratController.rowsPerPage.toString(),
        offset: (
          festivalVratController.page * festivalVratController.rowsPerPage
        ).toString(),
      });

      const url = `${GET_FESTIVAL_VRAT_ENDPOINT}?${queryParams}`;
      console.log("Fetching from URL:", url);

      const response = await fetch(url, { headers });

      if (!response.ok) {
        throw new Error("Failed to fetch festival & vrat data");
      }

      const jsonData = await response.json();
      console.log("GET Festival & Vrat Data:", jsonData);

      setFestivalVratData(jsonData.results || []);
      setFestivalVratRowCount(jsonData.count || 0);
    } catch (error) {
      console.error("Error fetching festival & vrat data:", error.message);
      setFestivalVratData([]);
      setFestivalVratRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchUploadLogs = async () => {
    console.log("Fetching upload logs...");

    try {
      const queryParams = new URLSearchParams({
        limit: logsController.rowsPerPage.toString(),
        offset: (logsController.page * logsController.rowsPerPage).toString(),
        ordering: "-created_at",
      });

      const url = `${GET_UPLOAD_LOGS_ENDPOINT}?${queryParams}`;
      console.log("Fetching from URL:", url);

      const response = await fetch(url, { headers });

      if (!response.ok) {
        throw new Error("Failed to fetch upload logs");
      }

      const jsonData = await response.json();
      console.log("GET Upload Logs:", jsonData);

      setUploadLogs(jsonData.results || []);
      setLogsRowCount(jsonData.count || 0);
    } catch (error) {
      console.error("Error fetching upload logs:", error.message);
      setUploadLogs([]);
      setLogsRowCount(0);
    }
  };

  // Add this utility function near your date formatting function
  const formatTimeWithSeconds = (timeString) => {
    if (!timeString) return "-";

    try {
      // If it's already in HH:MM:SS format, return as is
      if (timeString.includes(":") && timeString.split(":").length === 3) {
        return timeString;
      }

      // If it's in HH:MM format, add seconds
      if (timeString.includes(":") && timeString.split(":").length === 2) {
        return `${timeString}:00`;
      }

      // For AM/PM formats, parse and format with seconds
      const timeParts = timeString.split(" ");
      if (timeParts.length === 2) {
        const [timePart, period] = timeParts;
        if (timePart.split(":").length === 2) {
          return `${timePart}:00 ${period}`;
        }
      }

      return timeString; // Return original if we can't parse
    } catch (error) {
      console.error("Error formatting time:", error);
      return timeString;
    }
  };

  const fetchDashboardStats = async () => {
    console.log("Fetching dashboard stats...");

    try {
      const queryParams = new URLSearchParams({
        year: selectedYear,
      });

      const url = `${GET_KALNIRNAY_DASHBOARD_STATS_ENDPOINT}?${queryParams}`;
      console.log("Fetching from URL:", url);

      const response = await fetch(url, { headers });

      if (!response.ok) {
        throw new Error("Failed to fetch dashboard stats");
      }

      const jsonData = await response.json();
      console.log("GET Dashboard Stats:", jsonData);

      if (jsonData.success) {
        setStats(jsonData.statistics);
      }
    } catch (error) {
      console.error("Error fetching dashboard stats:", error.message);
      setStats(null);
    }
  };

  // Check if data exists
  const checkDataExists = async () => {
    console.log(
      `Checking if data exists for ${uploadMonth} ${uploadYear}, type: ${uploadType}`,
    );

    try {
      const url = PUT_CHECK_DATA_ENDPOINT;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Tenant-ID": tenant,
        },
        body: JSON.stringify({
          upload_type: uploadType,
          month: uploadMonth,
          year: uploadYear,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to check data existence");
      }

      const result = await response.json();
      console.log("Data exists check:", result);

      if (result.success && result.data.exists) {
        // Data exists, show confirmation dialog
        setExistingDataInfo(result.data);
        setConfirmDialogOpen(true);
      } else {
        // No existing data, proceed with upload
        performUpload(false);
      }
    } catch (error) {
      console.error("Error checking data existence:", error.message);
      setUploadMessage({
        type: "error",
        text: `Failed to check existing data: ${error.message}`,
      });
    }
  };

  // Helper function to format error messages for better readability
  const getFriendlyErrorMessage = (message) => {
  if (!message) return "Upload failed. Please verify the file and try again.";

  if (message.includes("Extra columns found")) {
    const typeLabel = SAMPLE_FILES[uploadType]?.label || "correct";
    return `Invalid file format. Please upload a valid ${typeLabel} CSV file using the sample template.`;
  }

  if (message.includes("utf-8") || message.includes("codec can't decode")) {
    return "The uploaded file contains unsupported characters. Please save the CSV file in UTF-8 format and try again.";
  }

  return message;
};
  const formatErrorMessage = (message) => {
    if (!message) return "Unknown error";

    // Remove redundant "Invalid Indian date format" prefix if present
    return message.replace(
      /^Invalid Indian date format: '[^']*'\. Error: /,
      "",
    );
  };

  // Add this utility function at the top of your component
  const formatIndianDate = (dateString) => {
    if (!dateString) return "-";

    try {
      // Parse the date string (assuming it comes as YYYY-MM-DD from backend)
      const date = new Date(dateString);

      // Format as DD/MM/YYYY
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();

      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString; // Return original if parsing fails
    }
  };

  // Handle file selection
  const handleFileSelect = (event, type) => {
    const file = event?.target?.files?.[0];

    if (!file) {
      console.warn("No file selected");
      return;
    }

    // Validate file type
    if (!file.name.endsWith(".csv")) {
      setUploadMessage({
        type: "error",
        text: "Invalid file type. Please upload a .csv file.",
      });
      event.target.value = ""; // Reset input
      return;
    }

    console.log(`File selected: ${file.name}, type: ${type}`);
    setSelectedFile(file);
    setUploadType(type);

    // Clear any previous messages
    setUploadMessage(null);
  };

  // Start upload process
  const handleUploadClick = () => {
    if (!selectedFile) {
      setUploadMessage({
        type: "error",
        text: "Please select a file first",
      });
      return;
    }

    if (!uploadMonth || !uploadYear) {
      setUploadMessage({
        type: "error",
        text: "Please select month and year",
      });
      return;
    }

    // Check if data exists
    checkDataExists();
  };

  // Perform actual upload
  // Perform actual upload
  // Perform actual upload
  const performUpload = async (overwrite) => {
    console.log(
      `Uploading file: ${selectedFile.name}, overwrite: ${overwrite}`,
    );
    setUploading(true);
    setUploadMessage(null);
    setConfirmDialogOpen(false);

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("upload_type", uploadType);
    formData.append("month", uploadMonth);
    formData.append("year", uploadYear);
    formData.append("overwrite", overwrite.toString());

    try {
      const url = PUT_UPLOAD_ENDPOINT;
      console.log("Uploading to URL:", url);

      const response = await fetch(url, {
        method: "POST",
        body: formData,
        headers: {
          "X-Tenant-ID": tenant,
        },
      });

      const result = await response.json();
      console.log("Upload API response:", result);

      if (result.success) {
        console.log("Upload successful");

        // Show results dialog
        setUploadResults(result);
        setResultsDialogOpen(true);
        setUploadDialogOpen(false);

        // Refresh all data
        fetchPanchangData();
        fetchMuhuratData();
        fetchFestivalVratData();
        fetchUploadLogs();
        fetchDashboardStats();
      } else {
        // Handle validation errors
        console.error("Upload failed:", result.error);

        const errorResult = {
          success: false,
          message: getFriendlyErrorMessage(
            result.error || "Upload failed due to validation errors",
          ),
          upload_log: {
            status: "failed",
            total_rows: result.errors ? result.errors.length : 0,
            processed_rows: 0,
            failed_rows: result.errors ? result.errors.length : 0,
            success_rate: 0,
          },
          warnings: [],
          errors: result.errors || [],
        };

        // If no detailed errors but there's a general error
        if (!result.errors && result.error) {
          errorResult.errors = [
            {
              row: "File",
              error_type: result.error_type || "Validation Error",
              message: result.error,
              field: "General",
              value: "N/A",
            },
          ];
        }

        setUploadResults(errorResult);
        setResultsDialogOpen(true);
      }
    } catch (error) {
      console.error("Upload error:", error.message);

      const errorResult = {
        success: false,
        message: "Upload failed due to network error",
        upload_log: {
          status: "failed",
          total_rows: 0,
          processed_rows: 0,
          failed_rows: 1,
          success_rate: 0,
        },
        warnings: [],
        errors: [
          {
            row: "Network",
            error_type: "Network Error",
            message: error.message,
            field: "Connection",
            value: "N/A",
          },
        ],
      };

      setUploadResults(errorResult);
      setResultsDialogOpen(true);
    } finally {
      // ✅ CRITICAL FIX: Always clear file inputs after upload attempt
      setUploading(false);
      resetFileInputs();
    }
  };

  // ✅ NEW FUNCTION: Clear file inputs and state
  const resetFileInputs = () => {
    // Clear React state
    setSelectedFile(null);
    setUploadType("");

    // Clear DOM file inputs (breaks browser file cache)
    const panchangInput = document.getElementById("panchang-upload");
    const muhuratInput = document.getElementById("muhurat-upload");

    if (panchangInput) panchangInput.value = "";
    if (muhuratInput) muhuratInput.value = "";

    console.log("File inputs cleared - ready for fresh file selection");
  };

  // Load data on mount and when dependencies change
  useEffect(() => {
    console.log("Effect triggered - Loading data...");
    fetchPanchangData();
    fetchDashboardStats();
  }, [
    selectedMonth,
    selectedYear,
    panchangController.page,
    panchangController.rowsPerPage,
  ]);

  useEffect(() => {
    fetchMuhuratData();
  }, [
    selectedMonth,
    selectedYear,
    muhuratController.page,
    muhuratController.rowsPerPage,
  ]);

  useEffect(() => {
    fetchFestivalVratData();
  }, [
    selectedMonth,
    selectedYear,
    festivalVratController.page,
    festivalVratController.rowsPerPage,
  ]);

  useEffect(() => {
    fetchUploadLogs();
  }, [logsController.page, logsController.rowsPerPage]);

  // Pagination handlers
  const handlePanchangPageChange = (event, newPage) => {
    console.log("Panchang page changed to:", newPage);
    setPanchangController((prev) => ({ ...prev, page: newPage }));
  };

  const handlePanchangRowsPerPageChange = (event) => {
    console.log("Panchang rows per page changed to:", event.target.value);
    setPanchangController({
      rowsPerPage: parseInt(event.target.value, 20),
      page: 0,
    });
  };

  const handleMuhuratPageChange = (event, newPage) => {
    console.log("Muhurat page changed to:", newPage);
    setMuhuratController((prev) => ({ ...prev, page: newPage }));
  };

  const handleMuhuratRowsPerPageChange = (event) => {
    console.log("Muhurat rows per page changed to:", event.target.value);
    setMuhuratController({
      rowsPerPage: parseInt(event.target.value, 20),
      page: 0,
    });
  };

  const handleFestivalVratPageChange = (event, newPage) => {
    console.log("Festival & Vrat page changed to:", newPage);
    setFestivalVratController((prev) => ({ ...prev, page: newPage }));
  };

  const handleFestivalVratRowsPerPageChange = (event) => {
    console.log("Festival & Vrat rows per page changed to:", event.target.value);
    setFestivalVratController({
      rowsPerPage: parseInt(event.target.value, 20),
      page: 0,
    });
  };

  const handleLogsPageChange = (event, newPage) => {
    console.log("Logs page changed to:", newPage);
    setLogsController((prev) => ({ ...prev, page: newPage }));
  };

  const handleLogsRowsPerPageChange = (event) => {
    console.log("Logs rows per page changed to:", event.target.value);
    setLogsController({
      rowsPerPage: parseInt(event.target.value, 20),
      page: 0,
    });
  };

  const getStatusColor = (status) => {
    const statusMap = {
      success: "success",
      failed: "error",
      partial: "warning",
      processing: "info",
      pending: "default",
    };
    return statusMap[status] || "default";
  };

  const getStatusIcon = (status) => {
    const iconMap = {
      success: <CheckCircle fontSize="small" />,
      failed: <ErrorIcon fontSize="small" />,
      partial: <Warning fontSize="small" />,
      default: <Schedule fontSize="small" />,
    };
    return iconMap[status] || iconMap["default"];
  };

  const [dragActive, setDragActive] = useState(false);

  // Add these two helper functions with your other helper functions:
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];

      // Validate file type
      if (!file.name.endsWith(".csv")) {
        setUploadMessage({
          type: "error",
          text: "Invalid file type. Please upload a .csv file.",
        });
        return;
      }

      setSelectedFile(file);
      setUploadMessage(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  // UPDATE YOUR EXISTING handleFileInput FUNCTION TO THIS:
  const handleFileInput = (e) => {
    const file = e?.target?.files?.[0];

    if (!file) {
      return;
    }

    if (!file.name.endsWith(".csv")) {
      setUploadMessage({
        type: "error",
        text: "Invalid file type. Please upload a .csv file.",
      });
      e.target.value = "";
      return;
    }

    setSelectedFile(file);
    setUploadMessage(null);
  };

  return (
    <Box
      sx={{
        bgcolor: "#f5f5f5",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        py: 1,
        px: { xs: 1, md: 2 },
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          width: "100%",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          gap: 3,
        }}
      >
        {/* Header / Toolbar Section — two zones: an identity block (icon/title/subtitle),
            separated by a hairline rule from a single controls row where filters and
            actions sit together, left and right, on the same line. This is the structural
            change: filters and buttons now belong to the same "toolbar" row instead of
            buttons being paired with the title or isolated on their own line. */}
        <Paper
          elevation={0}
          sx={{
            px: { xs: 1.5, md: 2 },
            py: 0.75,
            flexShrink: 0,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          {/* Zone 1 — Identity: icon + title + subtitle, compact single block */}
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, minWidth: 0 }}>
            <CalendarMonth sx={{ fontSize: 19, color: "primary.main", flexShrink: 0, position: "relative", top: 2 }} />
            <Typography sx={{ ...typography.h1 }} noWrap>
              Panchang Data Manager
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              — {selectedMonth} {selectedYear}
            </Typography>
          </Box>

          {/* Hairline rule — visually separates "what page this is" from "what you can do on it" */}
          <Box sx={{ borderBottom: "1px solid", borderColor: "divider", my: 0.75 }} />

          {/* Zone 2 — Controls: filters and actions share one row. A slim connecting rule
              fills the space between the two clusters so the toolbar reads as one bridged
              component instead of two groups with dead space between them — filters and
              buttons stay exactly where they were, only the middle is now tied together. */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: { xs: "wrap", sm: "nowrap" },
              rowGap: 1,
              columnGap: 2,
            }}
          >
            {/* Filters cluster */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
              <AppDropdown
                endIcon={<KeyboardArrowDownOutlinedIcon />}
                menuItems={months.map((month) => ({
                  label: month,
                  value: month,
                }))}
                selectedValue={selectedMonth}
                onMenuSelect={(value) => {
                  setSelectedMonth(value);
                  setPanchangController({ page: 0, rowsPerPage: 20 });
                  setMuhuratController({ page: 0, rowsPerPage: 20 });
                }}
                sx={{ minWidth: 140, height: 36 }}
              >
                {selectedMonth || "Month"}
              </AppDropdown>
              <AppDropdown
                endIcon={<KeyboardArrowDownOutlinedIcon />}
                menuItems={years.map((year) => ({
                  label: year,
                  value: year,
                }))}
                selectedValue={selectedYear}
                onMenuSelect={(value) => {
                  setSelectedYear(value);
                  setPanchangController({ page: 0, rowsPerPage: 20 });
                  setMuhuratController({ page: 0, rowsPerPage: 20 });
                }}
                sx={{ minWidth: 110, height: 36 }}
              >
                {selectedYear || "Year"}
              </AppDropdown>
            </Box>

            {/* Connecting rule — fills the previously-empty middle so the two clusters
                read as one toolbar instead of two disconnected halves */}
            <Box
              sx={{
                flex: 1,
                mx: 1,
                borderTop: "1px dashed",
                borderColor: "divider",
                opacity: 0.6,
                display: { xs: "none", sm: "block" },
              }}
            />

            {/* Actions cluster — unchanged position, right-aligned */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
              <ButtonTwo
                variant="outlined"
                startIcon={<History />}
                onClick={() => setLogsDialogOpen(true)}
                sx={{ height: 36, borderRadius: 1.5, px: 2 }}
              >
                Upload Logs
              </ButtonTwo>
              <ButtonOne
                variant="contained"
                startIcon={<Upload />}
                onClick={() => setUploadDialogOpen(true)}
                sx={{ height: 36, borderRadius: 1.5, px: 2 }}
              >
                Upload Data
              </ButtonOne>
            </Box>
          </Box>

          {/* Alert Message */}
          <Collapse in={!!uploadMessage}>
            {uploadMessage && (
              <Alert
                severity={uploadMessage.type}
                sx={{ ...typography.bodyLarge, mt: 1 }}
                onClose={() => setUploadMessage(null)}
              >
                {uploadMessage.text}
              </Alert>
            )}
          </Collapse>
        </Paper>

        {/* Tabs + Table Section — fills remaining viewport height */}
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              p: 3,
              pb: 2.5,
              flexShrink: 0,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <CustomTabs
              value={activeTab}
              onChange={(e, val) => {
                console.log("Tab changed to:", val);
                setActiveTab(val);
              }}
              tabs={[
                {
                  label: (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CalendarMonth fontSize="small" />
                      <Typography variant="body2" fontWeight="medium">
                        Daily Panchang ({panchangRowCount})
                      </Typography>
                    </Box>
                  ),
                  value: 0,
                },
                {
                  label: (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Schedule fontSize="small" />
                      <Typography variant="body2" fontWeight="medium">
                        Shubh Muhurat ({muhuratRowCount})
                      </Typography>
                    </Box>
                  ),
                  value: 1,
                },
                {
                  label: (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Celebration fontSize="small" />
                      <Typography variant="body2" fontWeight="medium">
                        Festival & Vrat ({festivalVratRowCount})
                      </Typography>
                    </Box>
                  ),
                  value: 2,
                },
              ]}
            />
          </Box>

          {/* Table area: takes all remaining height, scrolls internally */}
          <Box sx={{ flex: 1, minHeight: 0, width: "100%", overflow: "auto" }}>
            {/* Panchang Tab */}
            {activeTab === 0 && (
              <AppTable
                columns={[
                  { id: 'date', label: 'Date', render: (item) => formatIndianDate(item.date) || "-" },
                  { id: 'marathi_month', label: 'Marathi Month', render: (item) => item.marathi_month || "-" },
                  { id: 'day', label: 'Day', render: (item) => item.day || "-" },
                  { id: 'tithi', label: 'Tithi', render: (item) => item.tithi || "-" },
                  { id: 'nakshatra', label: 'Nakshatra', render: (item) => item.nakshatra || "-" },
                  { id: 'yoga', label: 'Yog', render: (item) => item.yoga || "-" },
                  { id: 'karan', label: 'Karan', render: (item) => item.karan || "-" },
                  { id: 'moon_sign', label: 'Moon Sign', render: (item) => item.moon_sign || "-" },
                  { id: 'sunrise', label: 'Sunrise', render: (item) => formatTimeWithSeconds(item.sunrise) || "-" },
                  { id: 'sunset', label: 'Sunset', render: (item) => formatTimeWithSeconds(item.sunset) || "-" },
                  { id: 'rahu_kal', label: 'Rahu Kal', render: (item) => item.rahu_kal || "-" },
                  { id: 'shubha_kal', label: 'Shubha Kal', render: (item) => item.shubha_kal || "-" },
                  {
                    id: 'shubha_divas',
                    label: 'Shubha Divas',
                    render: (item) => (
                      <Chip
                        label={item.shubha_divas_display || (item.shubha_divas ? "Yes" : "No")}
                        color={item.shubha_divas ? "success" : "error"}
                        size="small"
                      />
                    ),
                  },
                  {
                    id: 'important_days',
                    label: 'Important Days',
                    render: (item) =>
                      item.important_days ? (
                        <Tooltip title={item.important_days} placement="top">
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 160,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              cursor: 'default',
                            }}
                          >
                            {item.important_days}
                          </Typography>
                        </Tooltip>
                      ) : "-",
                  },
                  {
                    id: 'thought_of_the_day',
                    label: 'Thought of the Day',
                    render: (item) =>
                      item.thought_of_the_day ? (
                        <Tooltip title={item.thought_of_the_day} placement="top">
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 160,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              cursor: 'default',
                            }}
                          >
                            {item.thought_of_the_day}
                          </Typography>
                        </Tooltip>
                      ) : "-",
                  },
                ]}
                rows={panchangData}
                rowKey={(item, i) => item.id ?? i}
                emptyMessage={loading ? <CircularProgress size={24} /> : "No panchang data available"}
                count={panchangRowCount}
                page={panchangController.page}
                rowsPerPage={panchangController.rowsPerPage}
                rowsPerPageOptions={[20, 50, 100]}
                onPageChange={handlePanchangPageChange}
                onRowsPerPageChange={handlePanchangRowsPerPageChange}
              />
            )}

            {/* Muhurat Tab */}
            {activeTab === 1 && (
              <AppTable
                columns={[
                  { id: 'date', label: 'Date', render: (item) => formatIndianDate(item.date) || "-" },
                  { id: 'start_time', label: 'Start Time', render: (item) => formatTimeWithSeconds(item.start_time) || "-" },
                  { id: 'end_time', label: 'End Time', render: (item) => formatTimeWithSeconds(item.end_time) || "-" },
                  {
                    id: 'type',
                    label: 'Type',
                    render: (item) => (
                      <Chip
                        label={item.type || "Unknown"}
                        color="primary"
                        size="small"
                        variant="outlined"
                      />
                    ),
                  },
                ]}
                rows={muhuratData}
                rowKey={(item, i) => item.id ?? i}
                emptyMessage={loading ? <CircularProgress size={24} /> : "No muhurat data available"}
                count={muhuratRowCount}
                page={muhuratController.page}
                rowsPerPage={muhuratController.rowsPerPage}
                rowsPerPageOptions={[20, 50, 100]}
                onPageChange={handleMuhuratPageChange}
                onRowsPerPageChange={handleMuhuratRowsPerPageChange}
              />
            )}

            {/* Festival & Vrat Tab */}
            {activeTab === 2 && (
              <>
                <AppTable
                  columns={[
                    { id: 'festival', label: 'Festival', render: (item) => item.festival || "-" },
                    { id: 'festival_date', label: 'Festival Date', render: (item) => formatIndianDate(item.festival_date) || "-" },
                    { id: 'reminder_date', label: 'Reminder Date', render: (item) => formatIndianDate(item.reminder_date) || "-" },
                    {
                      id: 'puja_timing',
                      label: 'Puja Timing',
                      render: (item) =>
                        item.puja_start && item.puja_end
                          ? `${formatTimeWithSeconds(item.puja_start)} – ${formatTimeWithSeconds(item.puja_end)}${item.puja_crosses_midnight ? " (+1 day)" : ""}`
                          : "-",
                    },
                    {
                      id: 'recipe_name',
                      label: 'Recipe Name',
                      render: (item) =>
                        item.recipe_name ? (
                          item.recipe_link ? (
                            <Typography
                              component="a"
                              href={item.recipe_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              variant="body2"
                              sx={{ color: "primary.main", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                            >
                              {item.recipe_name}
                            </Typography>
                          ) : (
                            item.recipe_name
                          )
                        ) : "-",
                    },
                  ]}
                  rows={festivalVratData}
                  rowKey={(item, i) => item.id ?? i}
                  emptyMessage={loading ? <CircularProgress size={24} /> : "No festival & vrat data available"}
                  count={festivalVratRowCount}
                  page={festivalVratController.page}
                  rowsPerPage={festivalVratController.rowsPerPage}
                  rowsPerPageOptions={[20, 50, 100]}
                  onPageChange={handleFestivalVratPageChange}
                  onRowsPerPageChange={handleFestivalVratRowsPerPageChange}
                />
              </>
            )}
          </Box>
        </Paper>

        <Dialog
          open={uploadDialogOpen}
          onClose={() => {
            if (!uploading) {
              setUploadDialogOpen(false);
              resetFileInputs();
            }
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h5" fontWeight={600}>
              Upload CSV File
            </Typography>
            <IconButton
              onClick={() => {
                setUploadDialogOpen(false);
                resetFileInputs();
              }}
              disabled={uploading}
            >
              <Close />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers>
            <Alert severity="info" sx={{ mb: 3 }}>
              Select the file type, month, and year for the data you're
              uploading
            </Alert>

            {/* File Type Selection */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>File Type *</InputLabel>
              <Select
                value={uploadType}
                label="File Type *"
                onChange={(e) => {
                  setUploadType(e.target.value);
                  setSelectedFile(null);
                  // Clear the file input
                  const fileInput =
                    document.getElementById("file-upload-input");
                  if (fileInput) fileInput.value = "";
                }}
                disabled={uploading}
              >
                <MenuItem value="">Select file type</MenuItem>
                <MenuItem value="panchang">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CalendarMonth fontSize="small" />
                    Daily Panchang
                  </Box>
                </MenuItem>
                <MenuItem value="muhurat">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Schedule fontSize="small" />
                    Shubh Muhurat
                  </Box>
                </MenuItem>
                <MenuItem value="festival_vrat">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Celebration fontSize="small" />
                    Festival & Vrat Companion
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {/* Month and Year Selection */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Month *</InputLabel>
                  <Select
                    value={uploadMonth}
                    label="Month *"
                    onChange={(e) => setUploadMonth(e.target.value)}
                    disabled={uploading}
                  >
                    {months.map((m) => (
                      <MenuItem key={m} value={m}>
                        {m}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Year *</InputLabel>
                  <Select
                    value={uploadYear}
                    label="Year *"
                    onChange={(e) => setUploadYear(e.target.value)}
                    disabled={uploading}
                  >
                    {years.map((y) => (
                      <MenuItem key={y} value={y}>
                        {y}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Typography sx={{ ...typography.bodyLarge, mb: 1 }}>
              Upload CSV File *
            </Typography>

            {/* File Upload Area */}
            {!selectedFile ? (
              <Paper
                variant="outlined"
                onDragEnter={uploadType ? handleDrag : undefined}
                onDragLeave={uploadType ? handleDrag : undefined}
                onDragOver={uploadType ? handleDrag : undefined}
                onDrop={uploadType ? handleDrop : undefined}
                sx={{
                  border: "2px dashed",
                  borderColor: dragActive ? "primary.main" : "grey.300",
                  bgcolor: dragActive
                    ? "action.hover"
                    : uploadType
                      ? "grey.50"
                      : "grey.100",
                  borderRadius: 2,
                  p: 4,
                  textAlign: "center",
                  cursor: uploadType ? "pointer" : "not-allowed",
                  opacity: uploadType ? 1 : 0.6,
                  "&:hover": uploadType
                    ? { borderColor: "primary.main", bgcolor: "action.hover" }
                    : {},
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    bgcolor: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 16px",
                  }}
                >
                  <CloudUpload sx={{ fontSize: 32, color: "white" }} />
                </Box>
                <Typography variant="body1" fontWeight={500} gutterBottom>
                  {uploadType
                    ? "Drag and drop your file here"
                    : "Please select a file type first"}
                </Typography>
                {uploadType && (
                  <>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      or
                    </Typography>
                    <input
                      accept=".csv"
                      style={{ display: "none" }}
                      id="file-upload-input"
                      type="file"
                      onChange={handleFileInput}
                      disabled={!uploadType || uploading}
                    />
                    <label htmlFor="file-upload-input">
                      <Button
                        variant="contained"
                        component="span"
                        startIcon={<CloudUpload />}
                        disabled={!uploadType || uploading}
                        sx={{ textTransform: "none" }}
                      >
                        Choose File
                      </Button>
                    </label>
                    {/* Sample File Download */}
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Download sample:{" "}
                        <Typography
                          variant="body2"
                          component="a"
                          href={SAMPLE_FILES[uploadType]?.url}
                          download={SAMPLE_FILES[uploadType]?.filename}
                          sx={{
                            textDecoration: "none",
                            cursor: "pointer",
                            color: "primary.main",
                            fontWeight: "bold",
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          {SAMPLE_FILES[uploadType]?.label} CSV
                        </Typography>
                      </Typography>
                    </Box>
                  </>
                )}
              </Paper>
            ) : (
              <Paper
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  p: 2,
                  bgcolor: "#f0fdf4",
                  borderColor: "#22c55e",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 1,
                    bgcolor: "#22c55e",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <DescriptionIcon sx={{ color: "white" }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={500} noWrap>
                    {selectedFile.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatFileSize(selectedFile.size)}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => setSelectedFile(null)}
                  sx={{ color: "error.main" }}
                  disabled={uploading}
                >
                  <DeleteIcon />
                </IconButton>
              </Paper>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <ButtonTwo
              onClick={() => {
                setUploadDialogOpen(false);
                resetFileInputs();
              }}
              variant="outlined"
              sx={{ textTransform: "none" }}
              disabled={uploading}
            >
              Cancel
            </ButtonTwo>
            <ButtonOne
              onClick={handleUploadClick}
              variant="contained"
              disabled={
                !uploadType ||
                !uploadMonth ||
                !uploadYear ||
                !selectedFile ||
                uploading
              }
              startIcon={
                uploading ? <CircularProgress size={20} /> : <Upload />
              }
              sx={{ textTransform: "none" }}
            >
              {uploading ? "Uploading..." : "Upload"}
            </ButtonOne >
          </DialogActions>
        </Dialog>
        {/* Confirmation Dialog */}
        <Dialog open={confirmDialogOpen} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Warning color="warning" />
              <Typography variant="h6" fontWeight="bold">
                Data Already Exists
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              Data already exists for{" "}
              <strong>
                {existingDataInfo?.month} {existingDataInfo?.year}
              </strong>
              .
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Found <strong>{existingDataInfo?.count}</strong> existing records.
            </Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              Do you want to overwrite it?
            </Typography>
            {uploadType === "muhurat" && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                For Muhurat data, all existing records for this month will be
                deleted and replaced with new data.
              </Alert>
            )}
            {uploadType === "panchang" && (
              <Alert severity="info" sx={{ mt: 2 }}>
                For Panchang data, existing records will be updated with new
                values.
              </Alert>
            )}
            {uploadType === "festival_vrat" && (
              <Alert severity="info" sx={{ mt: 2 }}>
                For Festival & Vrat data, existing records will be updated
                with new values.
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDialogOpen(false)} color="inherit">
              No, Cancel
            </Button>
            <Button
              onClick={() => performUpload(true)}
              variant="contained"
              color="warning"
            >
              Yes, Overwrite
            </Button>
          </DialogActions>
        </Dialog>

        {/* Upload Results Dialog */}
        {/* Upload Results Dialog */}
        <Dialog open={resultsDialogOpen} maxWidth="lg" fullWidth>
          <DialogTitle>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {uploadResults?.success ? (
                  <CheckCircle color="success" />
                ) : (
                  <ErrorIcon color="error" />
                )}
                <Typography variant="h6" fontWeight="bold">
                  {uploadResults?.success
                    ? "Upload Successful"
                    : "Upload Failed"}
                </Typography>
              </Box>
              <IconButton onClick={() => setResultsDialogOpen(false)}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            {uploadResults && (
              <>
                {/* Summary Card */}
                <Paper
                  sx={{
                    p: 2,
                    mb: 2,
                    bgcolor: uploadResults.success
                      ? "success.light"
                      : "error.light",
                    color: uploadResults.success
                      ? "success.contrastText"
                      : "error.contrastText",
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="caption" display="block">
                        Total Rows
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {uploadResults.upload_log?.total_rows || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="caption" display="block">
                        Processed
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {uploadResults.upload_log?.processed_rows || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="caption" display="block">
                        Failed
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {uploadResults.upload_log?.failed_rows ||
                          uploadResults.errors?.length ||
                          0}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="caption" display="block">
                        Status
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {uploadResults.success ? "SUCCESS" : "FAILED"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Main Message */}
                {uploadResults.message && (
                  <Alert
                    severity={uploadResults.success ? "success" : "error"}
                    sx={{ mb: 2 }}
                  >
                    {uploadResults.message}
                  </Alert>
                )}

                {/* Warnings */}
                {uploadResults.warnings &&
                  uploadResults.warnings.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        gutterBottom
                      >
                        <Warning
                          fontSize="small"
                          sx={{ verticalAlign: "middle", mr: 1 }}
                        />
                        Warnings ({uploadResults.warnings.length})
                      </Typography>
                      <List dense>
                        {uploadResults.warnings.map((warning, index) => (
                          <ListItem key={index}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <Warning color="warning" fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={`Row ${warning.row}`}
                              secondary={warning.message}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                {/* Detailed Validation Errors */}
                {uploadResults.errors && uploadResults.errors.length > 0 && (
                  <Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      gutterBottom
                    >
                      <ErrorIcon
                        fontSize="small"
                        sx={{ verticalAlign: "middle", mr: 1 }}
                      />
                      Validation Errors ({uploadResults.errors.length})
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      Upload was cancelled due to the following validation
                      errors. Please fix them in your CSV file and try again.
                    </Typography>

                    <Paper
                      variant="outlined"
                      sx={{ maxHeight: 400, overflow: "auto" }}
                    >
                      <TableContainer>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell
                                sx={{
                                  fontWeight: "bold",
                                  bgcolor: "background.paper",
                                }}
                              >
                                Row
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: "bold",
                                  bgcolor: "background.paper",
                                }}
                              >
                                Field
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: "bold",
                                  bgcolor: "background.paper",
                                }}
                              >
                                Error Type
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: "bold",
                                  bgcolor: "background.paper",
                                }}
                              >
                                Value
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: "bold",
                                  bgcolor: "background.paper",
                                }}
                              >
                                Error Message
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {uploadResults.errors.map((error, index) => (
                              <TableRow key={index} hover>
                                <TableCell>
                                  <Chip
                                    label={error.row}
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    fontWeight="medium"
                                  >
                                    {error.field || "Unknown"}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {error.error_type}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    fontFamily="monospace"
                                  >
                                    {error.value || "N/A"}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    color="error.main"
                                  >
                                    {formatErrorMessage(error.message)}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>

                    {/* Error Summary for large error sets */}
                    {uploadResults.errors.length > 10 && (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        Showing {uploadResults.errors.length} validation errors.
                        Scroll to see all errors.
                      </Alert>
                    )}
                  </Box>
                )}

                {/* Success message if no warnings or errors */}
                {uploadResults.success &&
                  (!uploadResults.warnings ||
                    uploadResults.warnings.length === 0) &&
                  (!uploadResults.errors ||
                    uploadResults.errors.length === 0) && (
                    <Alert severity="success">
                      All rows processed successfully with no warnings or
                      errors!
                    </Alert>
                  )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setResultsDialogOpen(false);
                // Auto-refresh data after successful upload
                if (uploadResults?.success) {
                  fetchPanchangData();
                  fetchMuhuratData();
                  fetchUploadLogs();
                  fetchDashboardStats();
                }
              }}
              variant="contained"
              color={uploadResults?.success ? "success" : "primary"}
            >
              {uploadResults?.success ? "View Updated Data" : "Close"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Upload Logs Dialog */}
        <Dialog
          open={logsDialogOpen}
          onClose={() => setLogsDialogOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <History />
                <Typography sx={{ ...typography.h1}}>
                  Upload History
                </Typography>
              </Box>
              <IconButton onClick={() => setLogsDialogOpen(false)}>
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <AppTable
              columns={[
                {
                  id: 'status',
                  label: 'Status',
                  render: (log) => (
                    <AppChip
                      icon={getStatusIcon(log.status)}
                      label={log.status?.toUpperCase() || "UNKNOWN"}
                      color={getStatusColor(log.status)}
                    />
                  ),
                },
                { id: 'file_name', label: 'File Name', render: (log) => log.file_name || "-" },
                {
                  id: 'upload_type',
                  label: 'Type',
                  render: (log) => (
                    <AppChip
                      label={log.upload_type_display || log.upload_type || "Unknown"}
                      variant="outlined"
                    />
                  ),
                },
                {
                  id: 'period',
                  label: 'Period',
                  render: (log) => `${log.data_month || "-"} ${log.data_year || ""}`,
                },
                {
                  id: 'created_at',
                  label: 'Date',
                  render: (log) => log.created_at ? dayjs(log.created_at).format("MMM DD, YYYY") : "-",
                },
              ]}
              rows={uploadLogs}
              rowKey={(log, i) => log.id ?? i}
              emptyMessage="No upload logs available"
              count={logsRowCount}
              page={logsController.page}
              rowsPerPage={logsController.rowsPerPage}
              rowsPerPageOptions={[ 20]}
              onPageChange={handleLogsPageChange}
              onRowsPerPageChange={handleLogsRowsPerPageChange}
            />
          </DialogContent>
        </Dialog>
      </Box>
    </Box>
  );
};

export default PanchangUI;